/**
 * Changelog page — reads `/api/changelog` (which parses /app/memory/CHANGELOG.md
 * at request time) and renders a vertical timeline of every shipped change.
 *
 * "Auto-updated" from the user's perspective: the source markdown file is
 * appended-to whenever a new feature/fix goes live, and this page reads the
 * file live (no cache) so the next reload always shows the latest entries.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Zap, Wrench, Palette, Gauge, BookOpen, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Tag → { icon, class } lookup. Chip colours live in `index.css` under
 * `.chip-tag-*` so dark mode can override them cleanly. Keeping only
 * icon + label in JS keeps the component theme-agnostic.                */
const TAG_STYLE = {
  feature: { icon: Sparkles, cls: "chip-tag-feature", label: "Feature" },
  fix:     { icon: Wrench,   cls: "chip-tag-fix",     label: "Fix" },
  polish:  { icon: Palette,  cls: "chip-tag-polish",  label: "Polish" },
  perf:    { icon: Gauge,    cls: "chip-tag-perf",    label: "Perf" },
  docs:    { icon: BookOpen, cls: "chip-tag-docs",    label: "Docs" },
  default: { icon: Zap,      cls: "chip-tag-default", label: "Update" },
};

function TagChip({ tag }) {
  const s = TAG_STYLE[tag] || TAG_STYLE.default;
  const Icon = s.icon;
  return (
    <span
      className={`${s.cls} inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest shrink-0`}
    >
      <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
      {s.label}
    </span>
  );
}

/* Very small markdown-in-bullet renderer — supports **bold**, *italic*,
   `code`, and raw URLs. Keeps the codebase free of a full markdown lib. */
function renderInline(text) {
  const parts = [];
  const rx = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={m.index}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(<code key={m.index} className="font-mono-tight bg-[#edf2f7] rounded px-1 py-0.5 text-[11px] text-[#2c7a7b]">{tok.slice(1, -1)}</code>);
    else parts.push(<em key={m.index}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function formatFriendlyDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Changelog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Cache-buster query param + explicit no-cache headers guarantee we
        // never serve a stale changelog after a fresh entry has been shipped.
        const { data } = await axios.get(`${API}/changelog?t=${Date.now()}`, {
          headers: { "Cache-Control": "no-cache" },
        });
        if (!cancelled) setEntries(data.entries || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load changelog");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-5 pt-2" data-testid="changelog-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Pages / Changelog</div>
        <h1 className="font-display text-4xl text-[#1a202c] flex items-center gap-3 mt-1">
          <Sparkles className="h-7 w-7 text-[#4fd1c5]" strokeWidth={2} />
          What&apos;s new
        </h1>
        <p className="text-sm text-[#718096] mt-1 max-w-2xl">
          Every improvement, fix and polish shipped to rank.os. Updated live from the source markdown — reload for the latest.
        </p>
      </div>

      {loading && (
        <div className="card-soft p-10 flex items-center justify-center gap-3 text-sm text-[#a0aec0]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading changelog…
        </div>
      )}

      {error && (
        <div className="card-soft p-6 text-sm text-[#c53030]" data-testid="changelog-error">
          Couldn&apos;t load the changelog: {error}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="card-soft p-10 text-center text-sm text-[#a0aec0]" data-testid="changelog-empty">
          No entries yet. Check back after the next update.
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="relative">
          {/* Vertical spine line running down the left */}
          <div className="absolute left-4 top-3 bottom-3 w-px bg-[#e2e8f0] hidden sm:block" aria-hidden />

          <ol className="space-y-6" data-testid="changelog-list">
            {entries.map((entry, i) => (
              <li key={`${entry.date}-${i}`} className="relative sm:pl-12" data-testid={`changelog-entry-${i}`}>
                {/* Timeline dot */}
                <div
                  className="absolute left-2.5 top-4 h-3 w-3 rounded-full bg-[#4fd1c5] ring-4 ring-white shadow-[0_0_0_1px_#4fd1c5] hidden sm:block"
                  aria-hidden
                />
                <div className="card-soft p-5 sm:p-6">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">
                        {formatFriendlyDate(entry.date)}
                      </div>
                      <h2 className="font-display text-xl text-[#1a202c] mt-0.5" data-testid={`changelog-title-${i}`}>
                        {entry.title}
                      </h2>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {entry.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#4a5568] leading-relaxed">
                        {b.tag ? (
                          <TagChip tag={b.tag} />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#cbd5e0] mt-2 shrink-0" aria-hidden />
                        )}
                        <span className="min-w-0 flex-1">{renderInline(b.text)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
